import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { GitMerge, Check } from 'lucide-react';
import { formatValueForDisplay } from './duplicateHelpers';

export const MergeComparisonDialog = ({
  open,
  onOpenChange,
  mergeType,
  comparisonData,
  selectedTarget,
  setSelectedTarget,
  fieldSelections,
  setFieldSelections,
  onSelectAll,
  onMerge,
  merging,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-amber-600" />
            Merge {mergeType === 'player' ? 'Players' : 'Parents'}
          </DialogTitle>
          <DialogDescription>
            Select which record to keep and choose field values for the merged record.
          </DialogDescription>
        </DialogHeader>

        {comparisonData && (
          <div className="space-y-4">
            {/* Target Selection */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <Label className="text-sm font-medium mb-2 block">Select Record to Keep</Label>
              <div className="grid grid-cols-2 gap-4">
                {['1', '2'].map(num => {
                  const key = mergeType === 'player' ? `player${num}` : `parent${num}`;
                  const item = comparisonData[key];

                  if (!item || !item.id) {
                    return (
                      <div key={num} className="p-4 border-2 border-red-200 rounded-lg bg-red-50">
                        <p className="text-red-600 text-sm">Record {num} data unavailable</p>
                      </div>
                    );
                  }

                  const nameField = mergeType === 'player' ? 'full_name' : 'name';
                  const isSelected = selectedTarget === item.id;

                  return (
                    <div
                      key={num}
                      onClick={() => setSelectedTarget(item.id)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-green-500 bg-green-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`merge-target-option-${num}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {isSelected && <Check className="h-4 w-4 text-green-600" />}
                        <span className="font-medium">{item[nameField] || 'Unknown'}</span>
                      </div>
                      {mergeType === 'player' ? (
                        <p className="text-sm text-slate-500">{item.enrollment_count || 0} enrollments</p>
                      ) : (
                        <p className="text-sm text-slate-500">{item.children_count || 0} children</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                The other record will be merged into this one and deleted.
              </p>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onSelectAll('target')}>
                Select All from Record to Keep
              </Button>
              <Button variant="outline" size="sm" onClick={() => onSelectAll('source')}>
                Select All from Other Record
              </Button>
            </div>

            {/* Field Comparison Table */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-3 font-medium text-sm">Field</th>
                    <th className="text-left p-3 font-medium text-sm">Record 1</th>
                    <th className="text-left p-3 font-medium text-sm">Record 2</th>
                    <th className="text-center p-3 font-medium text-sm">Use</th>
                  </tr>
                </thead>
                <tbody>
                  {(comparisonData.comparison || []).map((field, idx) => {
                    const key1 = mergeType === 'player' ? 'player1' : 'parent1';
                    const record1 = comparisonData[key1];
                    const isTarget1 = record1 && selectedTarget === record1.id;

                    return (
                      <tr key={idx} className={field.different ? 'bg-amber-50' : ''}>
                        <td className="p-3 font-medium text-sm capitalize">
                          {field.field.replace(/_/g, ' ')}
                          {field.different && (
                            <Badge variant="outline" className="ml-2 text-xs">Different</Badge>
                          )}
                        </td>
                        <td className={`p-3 text-sm ${isTarget1 ? 'font-medium' : ''}`}>
                          {formatValueForDisplay(field.player1_value ?? field.parent1_value)}
                        </td>
                        <td className={`p-3 text-sm ${!isTarget1 ? 'font-medium' : ''}`}>
                          {formatValueForDisplay(field.player2_value ?? field.parent2_value)}
                        </td>
                        <td className="p-3 text-center">
                          <Select
                            value={fieldSelections[field.field] || 'target'}
                            onValueChange={(val) =>
                              setFieldSelections({ ...fieldSelections, [field.field]: val })
                            }
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="target">Keep Target</SelectItem>
                              <SelectItem value="source">Use Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onMerge}
            disabled={merging || !selectedTarget}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="confirm-merge-btn"
          >
            {merging ? 'Merging...' : 'Confirm Merge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
